// Whitelist sanitizer for agent-authored rich text that is rendered as HTML on the
// PUBLIC agent landing page (unauthenticated visitors). Keeps only the formatting the
// RichEditor produces (bold/italic/underline, lists, links, line breaks) and strips
// everything else — scripts, styles, event handlers, and unsafe URLs — so a stored
// bio can never execute in a visitor's browser.
//
// DOMParser builds an INERT document (no scripts run, no images load) while we clean,
// so constructing the tree here is itself safe; the result is only ever re-inserted
// after this pass has removed anything dangerous.
const ALLOWED = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'A', 'UL', 'OL', 'LI', 'P', 'BR', 'SPAN', 'DIV']);
const SAFE_URL = /^(https?:|mailto:|tel:)/i;

function clean(node) {
  // Snapshot children — we mutate (remove / unwrap) as we go.
  Array.from(node.childNodes).forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) return;          // plain text is safe
    if (child.nodeType !== Node.ELEMENT_NODE) { child.remove(); return; }  // comments, etc.

    const tag = child.tagName;
    if (!ALLOWED.has(tag)) {
      if (tag === 'SCRIPT' || tag === 'STYLE') { child.remove(); return; }
      // Unknown-but-harmless tag: sanitize its contents, then unwrap (keep the text/children).
      clean(child);
      while (child.firstChild) node.insertBefore(child.firstChild, child);
      child.remove();
      return;
    }

    // Strip every attribute except a safe href on <a>.
    Array.from(child.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (tag === 'A' && name === 'href' && SAFE_URL.test(attr.value.trim())) return;
      child.removeAttribute(attr.name);
    });
    if (tag === 'A') {
      child.setAttribute('target', '_blank');
      child.setAttribute('rel', 'noopener noreferrer nofollow');
    }

    clean(child);   // recurse into the (now-cleaned) element
  });
}

export function sanitizeHtml(html) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(String(html), 'text/html');
  clean(doc.body);
  return doc.body.innerHTML;
}

// True when HTML has real content (not just empty tags / <br> / whitespace) — used to
// decide whether to persist a bio at all.
export function htmlHasContent(html) {
  return String(html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim().length > 0;
}
